"use client";

import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AnimatePresence, motion } from "motion/react";
import { format, toDate } from "date-fns";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify-icon/react";
import { ErrorMessage } from "@hookform/error-message";
import * as z from "zod";

import { toast } from "@/utils/customToast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { useProfileDataStore } from "@/features/profile/tutor/store/useProfileDataStore";
import { CalendarWithYearSelect } from "@/components/ui/CalendarWithYear";
import { cn } from "@/lib/utils";
import { editTutorProfile } from "@/features/profile/tutor/api/editProfile.ts";
import { getDateFnsLocale } from "@/i18n/getDateFnsLocale.ts";
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils.ts";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { InformationTooltip } from "@/components/InformationTooltip";
import { useProfileEditFooterStore } from "@/features/profile/tutor/store/useProfileEditFooterStore";

interface EducationEditProps {
  onSave: () => void;
}

enum ViewState {
  EDUCATION_LIST = "EDUCATION_LIST",
  ADD_EDIT_EDUCATION = "ADD_EDIT_EDUCATION",
}

interface EducationItem {
  institution_name: string;
  degree?: string;
  field_of_study?: string;
  start_date?: string;
  end_date?: string;
  description?: string;
}

export default function EducationEdit({ onSave }: EducationEditProps) {
  const { profileData, setToReload } = useProfileDataStore();
  const { t, i18n } = useTranslation(["profile", "common", "forms"], {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });

  const [currentView, setCurrentView] = useState<ViewState>(ViewState.EDUCATION_LIST);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [localEducation, setLocalEducation] = useState<EducationItem[]>([]);
  const [isFormInitialized, setIsFormInitialized] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const setFooter = useProfileEditFooterStore((s) => s.setFooter);
  const formatMonthYearDate = useCallback((dateString: string): string => {
    return format(toDate(dateString), "yyyy-MM-dd");
  }, []);

  // Initialize local education state
  useEffect(() => {
    if (profileData?.education_section) {
      setLocalEducation(
        profileData.education_section.map((edu) => ({
          institution_name: edu.institution_name ?? "",
          degree: edu.degree ?? "",
          field_of_study: edu.field_of_study ?? "",
          description: edu.description ?? "",
          start_date: edu.start_date ? formatMonthYearDate(edu.start_date) : undefined,
          end_date: edu.end_date ? formatMonthYearDate(edu.end_date) : undefined,
        })),
      );
    }
  }, [profileData, formatMonthYearDate]);

  // Ensure the form's field array is initialized with the loaded education entries.
  // The backend replaces the full list, so the form must contain ALL entries.
  useEffect(() => {
    if (!isFormInitialized) {
      form.reset({ education: localEducation });
      setIsFormInitialized(true);
    }
  }, [localEducation, isFormInitialized]);

  const defaultValues = useMemo(() => {
    return {
      education: localEducation.length > 0 ? localEducation : [],
    };
  }, [localEducation]);

  // Updated schema to allow empty education array and make all fields optional
  const educationSectionSchema = useMemo(
    () =>
      z.object({
        education: z
          .array(
            z
              .object({
                institution_name: z.string().min(3, t("forms:errors.institutionNameMin")).max(255),
                degree: z.string().max(255).optional(),
                field_of_study: z.string().max(255).optional(),
                start_date: z.string().optional(),
                end_date: z.string().optional(),
                description: z.string().max(1000).optional(),
              })
              .refine(
                (data) => {
                  if (data.start_date && data.end_date) {
                    return data.end_date >= data.start_date;
                  }

                  return true;
                },
                {
                  message: t("forms:errors.endDateAfterStartDate"),
                  path: ["end_date"],
                },
              ),
          )
          .optional()
          .default([]),
      }),
    [t],
  );

  // Single education validation schema
  const singleEducationSchema = useMemo(
    () =>
      z
        .object({
          institution_name: z.string().min(3, t("forms:errors.institutionNameMin")).max(255),
          degree: z.string().max(255).optional(),
          field_of_study: z.string().max(255).optional(),
          start_date: z.string().optional(),
          end_date: z.string().optional(),
          description: z.string().max(1000).optional(),
        })
        .refine(
          (data) => {
            if (data.start_date && data.end_date) {
              return data.end_date >= data.start_date;
            }

            return true;
          },
          {
            message: t("forms:errors.endDateAfterStartDate"),
            path: ["end_date"],
          },
        ),
    [t],
  );

  const form = useForm<{ education: EducationItem[] }>({
    resolver: zodResolver(educationSectionSchema),
    defaultValues: async () => defaultValues,
  });

  const { append, update, replace } = useFieldArray({
    control: form.control,
    name: "education",
    shouldUnregister: true,
  });

  // Single education form for add/edit
  const singleEducationForm = useForm<EducationItem>({
    resolver: zodResolver(singleEducationSchema),
    defaultValues: {
      institution_name: "",
      degree: "",
      field_of_study: "",
      start_date: undefined,
      end_date: undefined,
      description: "",
    },
    mode: "onChange",
  });

  // Explicit empty defaults used to clear the single-entry form (avoid retaining previous defaults)
  const EMPTY_EDUCATION_ENTRY: EducationItem = {
    institution_name: "",
    degree: "",
    field_of_study: "",
    start_date: undefined,
    end_date: undefined,
    description: "",
  };

  const formatDateRange = useCallback(
    (startDate?: string, endDate?: string) => {
      if (!startDate) return "";

      const start = format(new Date(startDate), "MMM yyyy", {
        locale: getDateFnsLocale(i18n.language),
      });

      if (!endDate) {
        return `${start} - ${t("forms:education.present")}`;
      }

      const end = format(new Date(endDate), "MMM yyyy", {
        locale: getDateFnsLocale(i18n.language),
      });

      return `${start} - ${end}`;
    },
    [i18n.language, t],
  );

  const handleAddEducation = useCallback(
    (data: EducationItem) => {
      const result = singleEducationSchema.safeParse(data);

      if (result.success) {
        const formattedData = {
          ...result.data,
          start_date: result.data.start_date ?? undefined,
          end_date: result.data.end_date ?? undefined,
        };

        if (editingIndex !== null) {
          // Update existing education
          const updatedEducation = [...localEducation];

          updatedEducation[editingIndex] = formattedData;
          setLocalEducation(updatedEducation);
          update(editingIndex, formattedData);
        } else {
          // Add new education
          const nextEducation = [...localEducation, formattedData];

          setLocalEducation(nextEducation);
          // Keep RHF field array in sync with the full list
          replace(nextEducation);
        }

        // Reset to explicit empty defaults so future additions start clean
        singleEducationForm.reset(EMPTY_EDUCATION_ENTRY);
        setEditingIndex(null);
        setCurrentView(ViewState.EDUCATION_LIST);

        return true;
      } else {
        // Set errors on the form
        Object.keys(result.error.flatten().fieldErrors).forEach((fieldName) => {
          singleEducationForm.trigger(fieldName as keyof EducationItem);
        });

        return false;
      }
    },
    [editingIndex, localEducation, append, update, singleEducationForm, singleEducationSchema],
  );

  const handleEditEducation = useCallback(
    (index: number) => {
      const education = localEducation[index];

      setEditingIndex(index);
      singleEducationForm.reset(education);
      setCurrentView(ViewState.ADD_EDIT_EDUCATION);
    },
    [localEducation, singleEducationForm],
  );

  const handleRemoveEducation = useCallback(
    (index: number) => {
      const updatedEducation = localEducation.filter((_, i) => i !== index);

      setLocalEducation(updatedEducation);
      // Keep RHF field array in sync with the full list
      replace(updatedEducation);
    },
    [localEducation, replace],
  );

  const submitAllEducation = async () => {
    try {
      setIsSaving(true);
      if (profileData) {
        // Backend replaces the full list; always send ALL entries (new + existing)
        await editTutorProfile(profileData.id, "education", { education: localEducation });
        toast.success(t("common:success"), {
          description: t("common:successMessage"),
        });
        onSave();
        setToReload(true);
      }
    } catch (error: any) {
      console.error(error);
      const errors = error.response?.data?.errors;

      if (errors) {
        Object.entries(errors).forEach(([key, values]) => {
          (values as string[]).forEach((value: string) => {
            form.setError(key as keyof typeof form.formState.errors, { message: value });
          });
        });
      }
      toast.error(t("common:error"), {
        description: t("common:errorMessage"),
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Animation variants
  const stepVariants = useMemo(
    () => ({
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          type: "spring",
          stiffness: 300,
          damping: 30,
        },
      },
      exit: {
        opacity: 0,
        transition: {
          duration: 0.2,
        },
      },
    }),
    [],
  );

  const cardVariants = useMemo(
    () => ({
      hidden: { opacity: 0, scale: 0.95 },
      visible: (i: number) => ({
        opacity: 1,
        scale: 1,
        transition: {
          type: "spring",
          stiffness: 300,
          damping: 30,
          delay: i * 0.1,
        },
      }),
      exit: {
        opacity: 0,
        scale: 0.95,
        transition: {
          duration: 0.2,
          ease: "easeInOut",
        },
      },
    }),
    [],
  );

  // Education List View
  const renderEducationListView = () => (
    <motion.div
      animate="visible"
      className="flex h-full flex-col"
      exit="exit"
      initial="hidden"
      variants={stepVariants}
    >
      <Card className="bg-side-accent/80 border-border/30 relative overflow-hidden">
        <div className="pointer-events-none absolute -top-12 -right-16 h-40 w-40 rounded-full bg-gradient-to-br from-indigo-400 to-cyan-500 opacity-10" />
        <div className="pointer-events-none absolute -bottom-10 -left-12 h-28 w-28 rounded-full bg-gradient-to-tr from-emerald-400 to-teal-500 opacity-10" />
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2 max-sm:flex-col">
            <div className="flex items-center gap-2">
              <div className="bg-background/60 flex items-center rounded-xl p-2">
                <Icon height="1.125rem" icon="mdi:school-outline" width="1.125rem" />
              </div>
              <CardTitle className="text-accent-foreground text-lg">
                {t("profile:educationSection.cards.list.title", { defaultValue: "Education" })}
              </CardTitle>
              <InformationTooltip
                description={t("profile:educationSection.tooltips.list", {
                  defaultValue: "Add your degrees and studies.",
                })}
              />
            </div>
            <Button
              className="h-9 px-3"
              size="sm"
              type="button"
              variant="foreground"
              onClick={() => {
                setEditingIndex(null);
                // Ensure a clean form when adding a new entry
                singleEducationForm.reset(EMPTY_EDUCATION_ENTRY);
                setCurrentView(ViewState.ADD_EDIT_EDUCATION);
              }}
            >
              <Icon className="mr-2" height="1rem" icon="mdi:plus" width="1rem" />
              {t("forms:education.addEducation")}
            </Button>
          </div>
          <CardDescription className="text-muted-foreground">
            {t("profile:educationSection.cards.list.description", {
              defaultValue: "Manage your education history",
            })}
          </CardDescription>
        </CardHeader>
        <CardContent className="pb-24">
          <div className="space-y-4 pb-2">
            <AnimatePresence mode="popLayout">
              {localEducation.map((education, index) => (
                <motion.div
                  key={index}
                  layout
                  animate="visible"
                  custom={index}
                  exit="exit"
                  initial="hidden"
                  variants={cardVariants}
                >
                  <Card className="bg-muted/60 border-border/30 hover:border-accent/30 hover:bg-accent/50 cursor-pointer transition-all">
                    <CardContent className="flex items-start justify-between gap-4 p-5">
                      <div
                        className="flex min-w-0 flex-1 items-start gap-4"
                        onClick={() => handleEditEducation(index)}
                      >
                        <div className="bg-background/60 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full">
                          <Icon
                            className="text-accent-foreground"
                            height="1.125rem"
                            icon="mdi:school"
                            width="1.125rem"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="text-accent-foreground mb-1 truncate text-base font-semibold">
                            {education.institution_name}
                          </h4>
                          <div className="space-y-1">
                            {education.degree && (
                              <p className="text-muted-foreground text-sm font-medium">
                                {education.degree}
                              </p>
                            )}
                            {education.field_of_study && (
                              <p className="text-muted-foreground text-sm">
                                {education.field_of_study}
                              </p>
                            )}
                            {(education.start_date || education.end_date) && (
                              <p className="text-muted-foreground text-xs font-medium">
                                {formatDateRange(education.start_date, education.end_date)}
                              </p>
                            )}
                            {education.description && (
                              <p className="text-muted-foreground mt-2 line-clamp-2 text-sm">
                                {education.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive flex-shrink-0"
                        size="sm"
                        type="button"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveEducation(index);
                        }}
                      >
                        <Icon height="1rem" icon="mdi:trash-outline" width="1rem" />
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>

            {localEducation.length === 0 && (
              <div className="flex items-center justify-center py-8 text-center">
                <div className="text-muted-foreground">
                  <Icon
                    className="mx-auto mb-3"
                    height="3rem"
                    icon="mdi:school-outline"
                    width="3rem"
                  />
                  <p className="mb-1 text-base font-medium">{t("forms:education.noEducation")}</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );

  // Add/Edit Education View
  const renderAddEditEducationView = () => (
    <motion.div
      animate="visible"
      className="flex h-full flex-col"
      exit="exit"
      initial="hidden"
      variants={stepVariants}
    >
      {/* Content */}
      <div className="min-h-0 flex-1">
        <div className="space-y-6 pb-2">
          {/* Institution & Timeline */}
          <Card className="bg-side-accent/80 border-border/30 relative overflow-hidden">
            <div className="pointer-events-none absolute -top-10 -right-14 h-36 w-36 rounded-full bg-gradient-to-br from-amber-400 to-pink-500 opacity-10" />
            <div className="pointer-events-none absolute -bottom-8 -left-10 h-28 w-28 rounded-full bg-gradient-to-tr from-emerald-400 to-teal-500 opacity-10" />
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="bg-background/60 flex items-center rounded-xl p-2">
                  <Icon
                    className="text-accent-foreground"
                    height="1.125rem"
                    icon="mdi:school"
                    width="1.125rem"
                  />
                </div>
                <CardTitle className="text-accent-foreground text-lg">
                  {t("forms:education.institutionDetails")}
                </CardTitle>
                <InformationTooltip
                  description={t("profile:educationSection.tooltips.institutionDetails", {
                    defaultValue: "Provide the institution and study period.",
                  })}
                />
              </div>
            </CardHeader>
            <CardContent className="pb-24">
              <div className="space-y-6">
                {/* Institution Name */}
                <FormField
                  control={singleEducationForm.control}
                  name="institution_name"
                  render={({ field }) => (
                    <FormItem>
                      <div className="text-muted-foreground mb-3 flex items-center gap-2 text-sm font-medium">
                        <Icon
                          className="text-accent-foreground"
                          height="1rem"
                          icon="mdi:bank"
                          width="1rem"
                        />
                        {t("forms:education.institutionName")}
                      </div>
                      <FormControl>
                        <div className="relative">
                          <Input
                            className="h-12 pr-12 pl-4 text-base transition-all focus-visible:ring-2 focus-visible:ring-offset-1"
                            placeholder={t("forms:education.institutionPlaceholder")}
                            {...field}
                          />
                          <div className="absolute inset-y-0 right-4 flex items-center">
                            <Icon
                              className={cn(
                                "transition-colors",
                                field.value.length >= 3
                                  ? "text-accent-foreground"
                                  : "text-muted-foreground",
                              )}
                              height="1rem"
                              icon={
                                field.value.length >= 3
                                  ? "mdi:check-circle"
                                  : "mdi:close-circle-outline"
                              }
                              width="1rem"
                            />
                          </div>
                        </div>
                      </FormControl>
                      <p className="text-destructive mt-2 min-h-[1.25rem] text-sm">
                        <ErrorMessage
                          errors={singleEducationForm.formState.errors}
                          name="institution_name"
                        />
                      </p>
                    </FormItem>
                  )}
                />

                {/* Timeline */}
                <div>
                  <div className="text-muted-foreground mb-3 flex items-center gap-2 text-sm font-medium">
                    <Icon
                      className="text-accent-foreground"
                      height="1rem"
                      icon="mdi:timeline-clock"
                      width="1rem"
                    />
                    {t("forms:education.timeline")}
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField
                      control={singleEducationForm.control}
                      name="start_date"
                      render={({ field }) => (
                        <FormItem>
                          <div className="text-muted-foreground mb-2 text-xs font-medium">
                            {t("forms:education.startDate")}
                          </div>
                          <Popover modal={false}>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  className={cn(
                                    "border-input bg-background h-12 w-full justify-start border px-4 text-left text-sm font-normal transition-all focus-visible:ring-2 focus-visible:ring-offset-1",
                                    !field.value && "text-muted-foreground",
                                  )}
                                  variant="outline"
                                >
                                  <Icon
                                    className="text-accent-foreground mr-3"
                                    height="1rem"
                                    icon="mdi:calendar-outline"
                                    width="1rem"
                                  />
                                  {field.value ? (
                                    <span className="text-accent-foreground">
                                      {format(new Date(field.value), "MMM yyyy", {
                                        locale: getDateFnsLocale(i18n.language),
                                      })}
                                    </span>
                                  ) : (
                                    <span>{t("forms:education.selectDate")}</span>
                                  )}
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="z-[5000] w-auto p-0">
                              <CalendarWithYearSelect
                                initialFocus
                                disabled={(date) => date > new Date()}
                                fromYearSelect={new Date().getFullYear() - 49}
                                language={i18n.language}
                                mode="single"
                                selected={field.value ? new Date(field.value) : undefined}
                                weekStartsOn={1}
                                onSelect={(date) =>
                                  field.onChange(format(date ?? new Date(), "yyyy-MM-dd"))
                                }
                              />
                            </PopoverContent>
                          </Popover>
                          <p className="text-destructive mt-2 min-h-[1.25rem] text-sm">
                            <ErrorMessage
                              errors={singleEducationForm.formState.errors}
                              name="start_date"
                            />
                          </p>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={singleEducationForm.control}
                      name="end_date"
                      render={({ field }) => (
                        <FormItem>
                          <div className="text-muted-foreground mb-2 text-xs font-medium">
                            {t("forms:education.endDate")}
                          </div>
                          <Popover modal={false}>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  className={cn(
                                    "border-input bg-background h-12 w-full justify-start border px-4 text-left text-sm font-normal transition-all focus-visible:ring-2 focus-visible:ring-offset-1",
                                    !field.value && "text-muted-foreground",
                                    !singleEducationForm.watch("start_date") && "opacity-50",
                                  )}
                                  disabled={!singleEducationForm.watch("start_date")}
                                  variant="outline"
                                >
                                  <Icon
                                    className="text-accent-foreground mr-3"
                                    height="1rem"
                                    icon="mdi:calendar-outline"
                                    width="1rem"
                                  />
                                  {field.value ? (
                                    <span className="text-accent-foreground">
                                      {format(new Date(field.value), "MMM yyyy", {
                                        locale: getDateFnsLocale(i18n.language),
                                      })}
                                    </span>
                                  ) : (
                                    <span>{t("forms:education.selectDate")}</span>
                                  )}
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="z-[5000] w-auto p-0">
                              <CalendarWithYearSelect
                                initialFocus
                                disabled={(date) => {
                                  const startDate = singleEducationForm.watch("start_date");

                                  return startDate ? date < toDate(startDate) : false;
                                }}
                                fromYearSelect={new Date().getFullYear() - 49}
                                language={i18n.language}
                                mode="single"
                                selected={field.value ? new Date(field.value) : undefined}
                                weekStartsOn={1}
                                onSelect={(date) =>
                                  field.onChange(format(date ?? new Date(), "yyyy-MM-dd"))
                                }
                              />
                            </PopoverContent>
                          </Popover>
                          <p className="text-destructive mt-2 min-h-[1.25rem] text-sm">
                            <ErrorMessage
                              errors={singleEducationForm.formState.errors}
                              name="end_date"
                            />
                          </p>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Academic Details & Description */}
          <Card className="bg-side-accent/80 border-border/30 relative overflow-hidden">
            <div className="pointer-events-none absolute -top-10 -right-14 h-36 w-36 rounded-full bg-gradient-to-tr from-purple-500 to-pink-500 opacity-10" />
            <div className="pointer-events-none absolute -bottom-8 -left-10 h-28 w-28 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 opacity-10" />
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="bg-background/60 flex items-center rounded-xl p-2">
                  <Icon
                    className="text-accent-foreground"
                    height="1.125rem"
                    icon="mdi:book-open-variant"
                    width="1.125rem"
                  />
                </div>
                <CardTitle className="text-accent-foreground text-lg">
                  {t("forms:education.academicDetails")}
                </CardTitle>
                <InformationTooltip
                  description={t("profile:educationSection.tooltips.academicDetails", {
                    defaultValue: "Add your degree, field of study and a short description.",
                  })}
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div className="space-y-6">
                  <FormField
                    control={singleEducationForm.control}
                    name="degree"
                    render={({ field }) => (
                      <FormItem>
                        <div className="text-muted-foreground mb-3 flex items-center gap-2 text-sm font-medium">
                          <Icon
                            className="text-accent-foreground"
                            height="1rem"
                            icon="mdi:certificate"
                            width="1rem"
                          />
                          {t("forms:education.degree")}
                        </div>
                        <FormControl>
                          <Input
                            className="h-12 pr-4 pl-4 text-base transition-all focus-visible:ring-2 focus-visible:ring-offset-1"
                            placeholder={t("forms:education.degreePlaceholder")}
                            {...field}
                          />
                        </FormControl>
                        <p className="text-destructive mt-2 min-h-[1.25rem] text-sm">
                          <ErrorMessage
                            errors={singleEducationForm.formState.errors}
                            name="degree"
                          />
                        </p>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={singleEducationForm.control}
                    name="field_of_study"
                    render={({ field }) => (
                      <FormItem>
                        <div className="text-muted-foreground mb-3 flex items-center gap-2 text-sm font-medium">
                          <Icon
                            className="text-accent-foreground"
                            height="1rem"
                            icon="mdi:book-open-outline"
                            width="1rem"
                          />
                          {t("forms:education.fieldOfStudy")}
                        </div>
                        <FormControl>
                          <Input
                            className="h-12 pr-4 pl-4 text-base transition-all focus-visible:ring-2 focus-visible:ring-offset-1"
                            placeholder={t("forms:education.fieldPlaceholder")}
                            {...field}
                          />
                        </FormControl>
                        <p className="text-destructive mt-2 min-h-[1.25rem] text-sm">
                          <ErrorMessage
                            errors={singleEducationForm.formState.errors}
                            name="field_of_study"
                          />
                        </p>
                      </FormItem>
                    )}
                  />
                </div>

                <div>
                  <FormField
                    control={singleEducationForm.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <div className="text-muted-foreground mb-3 flex items-center gap-2 text-sm font-medium">
                          <Icon
                            className="text-accent-foreground"
                            height="1rem"
                            icon="mdi:text"
                            width="1rem"
                          />
                          {t("forms:education.description")}
                        </div>
                        <FormControl>
                          <div className="relative">
                            <Textarea
                              className="border-input [field-sizing:content] min-h-[170px] resize-none rounded-xl border p-4 text-base focus-visible:ring-2 focus-visible:ring-offset-1"
                              placeholder={t("forms:education.descriptionPlaceholder")}
                              {...field}
                            />
                            <div className="text-muted-foreground absolute right-4 bottom-3 text-xs">
                              {field.value?.length || 0}/1000
                            </div>
                          </div>
                        </FormControl>
                        <p className="text-destructive mt-2 min-h-[1.25rem] text-sm">
                          <ErrorMessage
                            errors={singleEducationForm.formState.errors}
                            name="description"
                          />
                        </p>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer handled by persistent footer */}
    </motion.div>
  );

  // Register persistent footer content for this section
  useEffect(() => {
    const footerNode =
      currentView === ViewState.EDUCATION_LIST ? (
        <div className="flex w-full justify-end gap-3">
          <Button
            className="bg-muted/20 hover:bg-muted/30 border-border/50 rounded-xl"
            variant="outline"
            onClick={onSave}
          >
            {t("common:cancel")}
          </Button>
          <Button
            className="bg-accent-foreground/90 hover:bg-accent-foreground text-background rounded-xl"
            disabled={isSaving}
            type="button"
            onClick={submitAllEducation}
          >
            <Icon className="mr-2" height="1rem" icon="mdi:check-circle" width="1rem" />
            {isSaving ? t("common:saving") : t("common:save")}
          </Button>
        </div>
      ) : (
        <div className="flex w-full items-center justify-between gap-3">
          <Button
            className="bg-muted/20 hover:bg-muted/30 border-border/50 rounded-xl"
            type="button"
            variant="outline"
            onClick={() => {
              singleEducationForm.reset(EMPTY_EDUCATION_ENTRY);
              setEditingIndex(null);
              setCurrentView(ViewState.EDUCATION_LIST);
            }}
          >
            <Icon className="mr-2" height="1rem" icon="mdi:arrow-left" width="1rem" />
            {t("common:cancel")}
          </Button>
          <Button
            className="bg-accent-foreground/90 hover:bg-accent-foreground text-background"
            type="button"
            onClick={() => handleAddEducation(singleEducationForm.getValues())}
          >
            <Icon className="mr-2" height="1rem" icon="mdi:check-circle" width="1rem" />
            {t("common:save")}
          </Button>
        </div>
      );

    setFooter({ key: `education-${currentView}`, node: footerNode });

    return () => {
      // Do not clear to avoid flicker if another section sets its own footer immediately
    };
  }, [currentView, isSaving, t, onSave, submitAllEducation, singleEducationForm, setEditingIndex]);

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
    >
      <Form {...form}>
        <form
          className="flex h-full flex-col pb-4"
          onSubmit={(e) => {
            e.preventDefault();
            submitAllEducation();
          }}
        >
          <div className="flex h-full flex-col">
            <AnimatePresence mode="wait">
              {currentView === ViewState.EDUCATION_LIST ? (
                <motion.div
                  key="education-list"
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  initial={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                >
                  {renderEducationListView()}
                </motion.div>
              ) : (
                <motion.div
                  key="education-add-edit"
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  initial={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {renderAddEditEducationView()}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </form>
      </Form>
    </motion.div>
  );
}
